import React, { createContext, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import apiPlaca from '../services/apiPlaca';

interface AuthContextData {
  signOut(): void;
  verifyToken(token: string): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const history = useHistory();

  const verifyToken = useCallback(async (token: string) => {
    const { data } = await apiPlaca
      .post('/tokenValidate', { token })
      .then(response => response)
      .catch(err => err.response);

    if (data.result === 'error') {
      history.push('/unauthorized');
    }
  }, []);

  apiPlaca.interceptors.response.use(
    response => {
      return response;
    },
    async ({ response, config }) => {
      const originalReq = config;

      if (response.status === 401) {
        originalReq._retry = false;
      }
      return false;
    },
  );

  const verifyEnv = (): string => {
    const env =
      process.env.REACT_APP_ENVIRONMENT === 'dev'
        ? process.env.REACT_APP_URL_DEV
        : process.env.REACT_APP_URL_PROD;

    return env || 'https://swint.imobpower.com';
  };

  const signOut = useCallback(() => {
    window.location.href = verifyEnv();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        signOut,
        verifyToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
